import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../../../assets/contexts/ThemeContext';
import { applyAppLanguage } from '../../../assets/utils/comm/languageUtils';
import CustomSelect from '../../../components/widgets/CustomSelect';

/**
 * 기본 폰트 제어 컴포넌트
 * 원본: font-control.html
 */
const FontControl = () => {
  const { themeConfig, set } = useTheme();
  const { i18n } = useTranslation();
  const { pathname, search } = useLocation();
  const currentThemeConfig = themeConfig || {};
  const [fontPreview, setFontPreview] = React.useState(null);
  const themeColor = currentThemeConfig.themeColor || '#e97730';

  // 언어 변경 핸들러
  const handleLanguageChange = (lang) => {
    applyAppLanguage(lang, {
      onThemeLocale: (locale) => set?.('locale', locale, false),
      updateUrl: true,
      pathname,
      search,
    });
  };

  const parseFontStack = (fontStack) => {
    if (!fontStack) return { primary: 'Pretendard', fallback: '' };
    const fonts = fontStack.split(',').map(f => f.trim().replace(/^["']|["']$/g, ''));
    if (fonts.length === 0) return { primary: 'Pretendard', fallback: '' };
    const primary = fonts[0];
    const fallback = fonts.slice(1).join(', ');
    return { primary, fallback };
  };

  const updateFontStack = React.useCallback((primaryFont, fallbackFonts) => {
    let fontStack = '';
    if (primaryFont === 'system') {
      fontStack = 'system-ui';
    } else if (primaryFont === 'custom') {
      fontStack = fallbackFonts.trim();
    } else {
      fontStack = `"${primaryFont}"`;
      if (fallbackFonts) {
        fontStack += ', ' + fallbackFonts;
      } else {
        if (primaryFont !== 'Pretendard') {
          fontStack += ', "Pretendard"';
        }
        fontStack += ', "Noto Sans KR", Helvetica, Arial, serif';
      }
    }

    set('fontStack', fontStack);
    set('primaryFont', primaryFont);
    set('fallbackFonts', fallbackFonts || '');

    // CSS 변수 업데이트
    document.documentElement.style.setProperty('--bs-font-sans-serif', fontStack);
    document.body.style.fontFamily = fontStack;

    // 동적 스타일 추가
    const styleId = 'dynamic-font-style';
    let style = document.getElementById(styleId);
    if (!style) {
      style = document.createElement('style');
      style.id = styleId;
      document.head.appendChild(style);
    }
    style.textContent = `body, body *:not([class*="pe-7s-"]):not([class*="ti-"]):not([class^="pe-7s-"]):not([class^="ti-"]), p, h1, h2, h3, h4, h5, h6 { font-family: ${fontStack} !important; } [class*="pe-7s-"], [class^="pe-7s-"] { font-family: "Pe-icon-7-stroke" !important; } [class*="ti-"], [class^="ti-"] { font-family: "themify" !important; }`;

    // 미리보기 업데이트
    if (fontPreview) {
      fontPreview.style.fontFamily = fontStack;
    }
  }, [set, fontPreview]);

  React.useEffect(() => {
    const previewEl = document.getElementById('font-preview');
    if (previewEl) setFontPreview(previewEl);
  }, []);

  React.useEffect(() => {
    const currentStack = currentThemeConfig.fontStack || '"Pretendard", "Noto Sans KR", Helvetica, Arial, serif';
    const parsed = parseFontStack(currentStack);
    const primaryValue = parsed.primary === 'system-ui' ? 'system' :
                        (['Pretendard', 'Noto Sans KR', 'Helvetica', 'Arial'].includes(parsed.primary) ? parsed.primary : 'custom');

    if (primaryValue !== currentThemeConfig.primaryFont) {
      updateFontStack(primaryValue, parsed.fallback);
    }
  }, [currentThemeConfig.fontStack, currentThemeConfig.primaryFont, updateFontStack]);

  const currentParsed = parseFontStack(currentThemeConfig.fontStack || '"Pretendard", "Noto Sans KR", Helvetica, Arial, serif');
  const primaryValue = currentParsed.primary === 'system-ui' ? 'system' :
                      (['Pretendard', 'Noto Sans KR', 'Helvetica', 'Arial'].includes(currentParsed.primary) ? currentParsed.primary : 'custom');
  const fallbackValue = primaryValue === 'custom' ? (currentThemeConfig.fontStack || '') : currentParsed.fallback;

  return (
    <div id="font-control-section">
      {/* 다국어 변경 옵션 */}
      <div style={{ marginBottom: '15px', paddingBottom: '15px', borderBottom: '1px solid #eee' }}>
        <label style={{ display: 'block', fontSize: '11px', color: '#666', marginBottom: '8px', fontWeight: 'bold' }}>
          🌐 다국어 설정:
        </label>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            onClick={() => handleLanguageChange('ko')}
            style={{
              flex: 1,
              padding: '8px 12px',
              fontSize: '12px',
              fontWeight: i18n.language === 'ko' ? 600 : 400,
              color: i18n.language === 'ko' ? '#fff' : '#666',
              background: i18n.language === 'ko' ? themeColor : '#f5f5f5',
              border: `1px solid ${i18n.language === 'ko' ? themeColor : '#ddd'}`,
              borderRadius: '4px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            한글
          </button>
          <button
            type="button"
            onClick={() => handleLanguageChange('en')}
            style={{
              flex: 1,
              padding: '8px 12px',
              fontSize: '12px',
              fontWeight: i18n.language === 'en' ? 600 : 400,
              color: i18n.language === 'en' ? '#fff' : '#666',
              background: i18n.language === 'en' ? themeColor : '#f5f5f5',
              border: `1px solid ${i18n.language === 'en' ? themeColor : '#ddd'}`,
              borderRadius: '4px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            English
          </button>
        </div>
        <div style={{ fontSize: '10px', color: '#999', marginTop: '6px' }}>
          현재 언어: {i18n.language === 'ko' ? '한글' : 'English'}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
          <label style={{ fontWeight: 'bold', color: '#333', fontSize: '13px', margin: 0, flex: 1 }}>🔤 기본 폰트:</label>
          <button
            type="button"
            className="theme-help-btn"
            data-target="font-control-section"
            style={{
              background: 'transparent',
              border: '1px solid #ddd',
              color: '#666',
              fontSize: '11px',
              width: '20px',
              height: '20px',
              padding: 0,
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="이 항목으로 이동"
          >
            ?
          </button>
        </div>
        <button
          type="button"
          onClick={() => {
            set('primaryFont', 'Pretendard');
            set('fallbackFonts', '"Noto Sans KR", Helvetica, Arial, serif');
            updateFontStack('Pretendard', '"Noto Sans KR", Helvetica, Arial, serif');
          }}
          style={{
            background: 'transparent',
            border: '1px solid #ddd',
            color: '#666',
            fontSize: '11px',
            padding: '4px 10px',
            borderRadius: '3px',
            cursor: 'pointer',
            marginLeft: '6px'
          }}
        >
          리셋
        </button>
      </div>

      <div style={{ marginBottom: '12px' }}>
        <label style={{ display: 'block', fontSize: '11px', color: '#666', marginBottom: '4px' }}>주요 폰트:</label>
        <CustomSelect
          inputId="primary-font-selector"
          value={primaryValue}
          onChange={(selected) => {
            if (selected === 'custom') {
              updateFontStack('custom', fallbackValue);
            } else {
              updateFontStack(selected, fallbackValue);
            }
          }}
          isSearchable={false}
          isClearable={false}
          options={[
            { value: 'Pretendard', label: 'Pretendard' },
            { value: 'Noto Sans KR', label: 'Noto Sans KR' },
            { value: 'Helvetica', label: 'Helvetica' },
            { value: 'Arial', label: 'Arial' },
            { value: 'system', label: '시스템 폰트 (system-ui)' },
            { value: 'custom', label: '사용자 정의...' },
          ]}
          styles={{
            control: (base, state) => ({
              ...base,
              width: '100%',
              minHeight: 34,
              fontSize: 13,
              borderRadius: 4,
              borderColor: state.isFocused ? '#86b7fe' : '#ddd',
            }),
            valueContainer: (base) => ({ ...base, padding: '0 10px' }),
            indicatorsContainer: (base) => ({ ...base, height: 34 }),
          }}
        />
      </div>

      <div style={{ marginBottom: '12px' }}>
        <label style={{ display: 'block', fontSize: '11px', color: '#666', marginBottom: '4px' }}>추가 폰트 (선택사항):</label>
        <input
          type="text"
          id="fallback-fonts-input"
          value={fallbackValue}
          onChange={(e) => {
            const value = e.target.value;
            if (primaryValue === 'custom') {
              updateFontStack('custom', value);
            } else {
              updateFontStack(primaryValue, value);
            }
          }}
          placeholder={primaryValue === 'custom' ? '전체 폰트 스택을 입력하세요 (예: "Pretendard", "Noto Sans KR", serif)' : '"Noto Sans KR", Helvetica, Arial, serif'}
          style={{
            width: '100%',
            padding: '6px 10px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '12px',
            fontFamily: 'monospace'
          }}
        />
        <div style={{ fontSize: '10px', color: '#999', marginTop: '4px' }}>쉼표로 구분하여 입력하세요</div>
      </div>

      <div style={{ marginBottom: '8px' }}>
        <label style={{ display: 'block', fontSize: '11px', color: '#666', marginBottom: '4px' }}>미리보기:</label>
        <div
          id="font-preview"
          style={{
            padding: '12px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            background: '#f9f9f9',
            minHeight: '60px',
            fontSize: '14px',
            lineHeight: 1.6,
            fontFamily: currentThemeConfig.fontStack || '"Pretendard", "Noto Sans KR", Helvetica, Arial, serif'
          }}
        >
          <div>The quick brown fox jumps over the lazy dog</div>
          <div style={{ marginTop: '8px' }}>한글 미리보기 텍스트입니다. 가나다라마바사아자차카타파하</div>
          <div style={{ marginTop: '8px', fontWeight: 'bold' }}>Bold 텍스트</div>
          <div style={{ marginTop: '4px', fontSize: '12px' }}>Small 텍스트 (12px)</div>
        </div>
      </div>
    </div>
  );
};

export default FontControl;

